import { isFieldBinding } from "@redotech/effect-api-shared/default-rules/field-binding";
import { useHandler } from "@redotech/react-util/hook";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import XIcon from "@redotech/redo-icons/x_filled.svg";
import { StepType } from "@redotech/redo-model/advanced-flow/common";
import {
  AssignV3Strategy,
  SetStatusActionStatus,
} from "@redotech/redo-model/advanced-flow/rules/actions";
import {
  addTagActionSchema,
  assignActionSchema,
  assignV3ActionSchema,
  autoReplyActionSchema,
  handoffAiActionSchema,
  hideCommentActionSchema,
  likeCommentActionSchema,
  removeTagActionSchema,
  setPriorityActionSchema,
  setStatusActionSchema,
  setStatusV3ActionSchema,
  startAiAgentExecutionActionSchema,
  type RuleAction,
  type RuleActionsStep,
} from "@redotech/redo-model/advanced-flow/rules/schemas";
import {
  RULE_V3_ACTION_LABELS,
  RULE_V3_ACTION_TYPES,
  RuleActionType,
} from "@redotech/redo-model/advanced-flow/rules/types";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import * as inspectorStyles from "@redotech/redo-web/inspector.module.css";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { produce } from "immer";
import { memo, useMemo } from "react";
import { useDefaultRuleEditContext } from "../../../../rules/default-rule-edit-context";
import { useAvailableSteps } from "../hooks/use-available-steps";
import type {
  DeepPartial,
  FrontendStepType,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "../index";
import { AddTagAction } from "./actions/add-tag-action";
import { AssignAction } from "./actions/assign-action";
import { AssignV3Action } from "./actions/assign-v3-action";
import { AutoReplyAction } from "./actions/auto-reply-action";
import { RemoveTagAction } from "./actions/remove-tag-action";
import { SetPriorityAction } from "./actions/set-priority-action";
import { SetStatusAction } from "./actions/set-status-action";
import { SetStatusV3Action } from "./actions/set-status-v3-action";

const v2ActionTypeOptions: RedoListItem<RuleActionType>[] = [
  {
    id: RuleActionType.SET_STATUS,
    type: "text",
    text: "Set status",
    value: RuleActionType.SET_STATUS,
  },
  {
    id: RuleActionType.ADD_TAG,
    type: "text",
    text: "Add tag",
    value: RuleActionType.ADD_TAG,
  },
  {
    id: RuleActionType.AUTO_REPLY,
    type: "text",
    text: "Auto reply",
    value: RuleActionType.AUTO_REPLY,
  },
  {
    id: RuleActionType.ASSIGN,
    type: "text",
    text: "Assign to user",
    value: RuleActionType.ASSIGN,
  },
  {
    id: RuleActionType.LIKE_COMMENT,
    type: "text",
    text: "Like comment",
    value: RuleActionType.LIKE_COMMENT,
  },
  {
    id: RuleActionType.HIDE_COMMENT,
    type: "text",
    text: "Hide comment",
    value: RuleActionType.HIDE_COMMENT,
  },
];

// V3 options are derived from the single source of truth in
// `@redotech/redo-model/advanced-flow/rules/types`. To add, remove, or
// rename a V3 action, edit `RULE_V3_ACTION_TYPES` / `RULE_V3_ACTION_LABELS`
// there — the dropdown picks it up automatically. Legacy `ASSIGN` is not
// in the V3 list; `ASSIGN_V3` takes its place and is labeled just "Assign".
const v3ActionTypeOptions: RedoListItem<RuleActionType>[] =
  RULE_V3_ACTION_TYPES.map((type) => ({
    id: type,
    type: "text" as const,
    text: RULE_V3_ACTION_LABELS[type],
    value: type,
  }));

const ActionEditor = memo(function ActionEditor({
  action,
  index,
  onUpdate,
  onDelete,
  isV3,
}: {
  action: DeepPartial<RuleAction>;
  index: number;
  onUpdate: (updated: DeepPartial<RuleAction>) => void;
  onDelete: () => void;
  isV3: boolean;
}) {
  // Default-rule edit mode: the template fixes the action structure
  // (type + slot in the flow) and declares which fields inside the
  // action are editable via `FieldBinding` markers. Structural controls
  // (type dropdown, delete) lock; the leaf editor receives an
  // `editableFields` set derived from the template.
  const editCtx = useDefaultRuleEditContext();
  const structureLocked = editCtx !== null;
  const templateAction =
    editCtx && action.actionType
      ? editCtx.findActionTemplate(action.actionType)
      : undefined;
  const editableFields = useMemo<ReadonlySet<string> | undefined>(() => {
    if (!editCtx) {
      return undefined;
    }
    const set = new Set<string>();
    if (templateAction) {
      for (const [key, value] of Object.entries(templateAction)) {
        if (isFieldBinding(value)) {
          set.add(key);
        }
      }
    }
    return set;
  }, [editCtx, templateAction]);
  const actionTypeOptions = isV3 ? v3ActionTypeOptions : v2ActionTypeOptions;
  const ActionTypeInspector = useMemo(() => {
    if (!action.actionType) {
      return null;
    }
    const actionType: RuleActionType = action.actionType;

    switch (actionType) {
      case RuleActionType.SET_STATUS:
        return (
          <SetStatusAction
            action={setStatusActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      case RuleActionType.SET_STATUS_V3:
        return (
          <SetStatusV3Action
            action={setStatusV3ActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      case RuleActionType.SET_PRIORITY:
        return (
          <SetPriorityAction
            action={setPriorityActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      case RuleActionType.ADD_TAG:
        return (
          <AddTagAction
            action={addTagActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      case RuleActionType.REMOVE_TAG:
        return (
          <RemoveTagAction
            action={removeTagActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      case RuleActionType.AUTO_REPLY:
        return (
          <AutoReplyAction
            action={autoReplyActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      case RuleActionType.ASSIGN:
        return (
          <AssignAction
            action={assignActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      case RuleActionType.ASSIGN_V3:
        return (
          <AssignV3Action
            action={assignV3ActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      case RuleActionType.LIKE_COMMENT:
        likeCommentActionSchema.parse(action);
        return null;
      case RuleActionType.HIDE_COMMENT:
        hideCommentActionSchema.parse(action);
        return null;
      case RuleActionType.START_AI_AGENT_EXECUTION:
        startAiAgentExecutionActionSchema.parse(action);
        return null;
      case RuleActionType.HANDOFF_AI:
        handoffAiActionSchema.parse(action);
        return null;
      case RuleActionType.GENERATE_CONVERSATION_INSIGHTS:
        // Backend-only action — not in RULE_V3_ACTION_TYPES, so it never
        // appears in the dropdown. Case exists only to keep the switch
        // exhaustive if a rule with this action is loaded into the editor.
        return null;
      default:
        assertNever(actionType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action.actionType, action]);

  return (
    <Flex dir="column" gap="md">
      <Flex align="center" justify="space-between" w="full">
        <Text fontSize="sm" fontWeight="medium">
          Action {index + 1}
        </Text>
        {!structureLocked && (
          <RedoButton
            hierarchy="tertiary"
            IconLeading={TrashIcon}
            onClick={onDelete}
            size="sm"
          />
        )}
      </Flex>
      <RedoSingleSelectDropdownInput<RuleActionType>
        disabled={structureLocked}
        fitToAnchor
        label="Action type"
        options={actionTypeOptions}
        optionSelected={({ item }) => {
          const value = item.value;

          let newAction: DeepPartial<RuleAction>;

          switch (value) {
            case RuleActionType.SET_STATUS:
              newAction = {
                actionType: RuleActionType.SET_STATUS,
                status: SetStatusActionStatus.CLOSED,
              };
              break;
            case RuleActionType.SET_STATUS_V3:
              newAction = {
                actionType: RuleActionType.SET_STATUS_V3,
                status: "CLOSED",
              };
              break;
            case RuleActionType.SET_PRIORITY:
              newAction = {
                actionType: RuleActionType.SET_PRIORITY,
                priority: "URGENT",
              };
              break;
            case RuleActionType.ADD_TAG:
              newAction = { actionType: RuleActionType.ADD_TAG, tag: [] };
              break;
            case RuleActionType.REMOVE_TAG:
              newAction = { actionType: RuleActionType.REMOVE_TAG, tag: [] };
              break;
            case RuleActionType.AUTO_REPLY:
              newAction = {
                actionType: RuleActionType.AUTO_REPLY,
                textContent: "",
                htmlContent: "",
              };
              break;
            case RuleActionType.ASSIGN:
              newAction = {
                actionType: RuleActionType.ASSIGN,
                user: { id: "", name: "" },
              };
              break;
            case RuleActionType.ASSIGN_V3:
              newAction = {
                actionType: RuleActionType.ASSIGN_V3,
                mode: { _tag: "specific_users", mongoUserIds: [] },
                strategy: AssignV3Strategy.ROUND_ROBIN,
                maxTicketsPerUser: null,
              };
              break;
            case RuleActionType.LIKE_COMMENT:
              newAction = { actionType: RuleActionType.LIKE_COMMENT };
              break;
            case RuleActionType.HIDE_COMMENT:
              newAction = { actionType: RuleActionType.HIDE_COMMENT };
              break;
            case RuleActionType.START_AI_AGENT_EXECUTION:
              newAction = {
                actionType: RuleActionType.START_AI_AGENT_EXECUTION,
              };
              break;
            case RuleActionType.HANDOFF_AI:
              newAction = { actionType: RuleActionType.HANDOFF_AI };
              break;
            case RuleActionType.GENERATE_CONVERSATION_INSIGHTS:
              // Unreachable: this action is hidden from the dropdown via
              // `RULE_V3_ACTION_TYPES` / `v2ActionTypeOptions`. Case exists
              // purely for exhaustiveness.
              newAction = {
                actionType: RuleActionType.GENERATE_CONVERSATION_INSIGHTS,
              };
              break;
            default:
              assertNever(value);
          }

          onUpdate(newAction);
        }}
        placeholder="Select action type"
        selectedItem={
          action.actionType
            ? actionTypeOptions.find((opt) => opt.value === action.actionType)
            : undefined
        }
      />
      {action.actionType && ActionTypeInspector && ActionTypeInspector}
      {action.actionType === RuleActionType.LIKE_COMMENT && (
        <Text fontSize="xs" textColor="tertiary">
          Only supported for Facebook comment conversations.
        </Text>
      )}
    </Flex>
  );
});

const RuleActionsInspector = memo(function RuleActionsInspector({
  step,
  steps,
  setStep,
  onClose,
  setHoveredNodeId,
}: StepTypeDetailsProps<PartialWithRequiredType<RuleActionsStep>>) {
  // Structural controls (add-action, next-step routing) lock when the
  // step is part of a default-rule template.
  const structureLocked = useDefaultRuleEditContext() !== null;
  // This inspector is shared between the V2 (RULE_ACTIONS) and V3
  // (RULE_ACTIONS_V3) step types in step-types.ts. The step schemas are
  // structurally identical, so the cast is safe — we use it only to decide
  // which subset of actions to offer in the dropdown.
  const isV3 = (step.type as StepType) === StepType.RULE_ACTIONS_V3;
  const handleAddAction = useHandler(() => {
    const newActions = [
      ...(step.actions || []),
      {
        actionType: RuleActionType.SET_STATUS,
        status: SetStatusActionStatus.CLOSED,
      },
    ];
    setStep({
      ...step,
      actions: newActions as DeepPartial<RuleActionsStep["actions"]>,
    });
  });

  const handleDeleteAction = useHandler((index: number) => {
    const newActions = (step.actions || []).filter((_, i) => i !== index);
    setStep({
      ...step,
      actions: newActions as DeepPartial<RuleActionsStep["actions"]>,
    });
  });

  const handleUpdateAction = useHandler(
    (index: number, updated: DeepPartial<RuleAction>) => {
      const newActions = [...(step.actions || [])];
      newActions[index] = updated;
      setStep({
        ...step,
        actions: newActions as DeepPartial<RuleActionsStep["actions"]>,
      });
    },
  );

  const nextStep = step.nextId
    ? steps.find((s) => s.id === step.nextId)
    : undefined;

  const { availableSteps, createConnectionHandler, onItemHover } =
    useAvailableSteps(step, steps, setStep, setHoveredNodeId);

  const handleNextStepSelected = createConnectionHandler(0);

  return (
    <Flex dir="column" gap="xl" justify="flex-start">
      <Flex justify="space-between" w="full">
        <Text fontSize="lg" fontWeight="semibold">
          Rule actions
        </Text>
        {onClose && (
          <RedoButton
            hierarchy="tertiary"
            IconLeading={XIcon}
            onClick={onClose}
            size="sm"
          />
        )}
      </Flex>

      <Text fontSize="sm" textColor="secondary">
        Add actions to perform when conditions match. Actions are executed in
        order.
      </Text>

      <Flex dir="column" gap="lg">
        {step.actions?.map((action, index) => (
          <>
            {index > 0 && <Divider />}
            <ActionEditor
              action={action}
              index={index}
              isV3={isV3}
              key={index}
              onDelete={() => handleDeleteAction(index)}
              onUpdate={(updated) => handleUpdateAction(index, updated)}
            />
          </>
        ))}

        {(!step.actions || step.actions.length === 0) && (
          <Flex className={inspectorStyles.emptyState} dir="column" gap="sm">
            <Text fontSize="sm" textColor="tertiary">
              No actions added yet. At least one action is required.
            </Text>
          </Flex>
        )}

        {!structureLocked && (
          <RedoButton
            hierarchy="secondary"
            IconLeading={PlusIcon}
            onClick={handleAddAction}
            size="md"
          >
            Add action
          </RedoButton>
        )}
      </Flex>

      <Divider />

      <Flex dir="column" gap="lg">
        <Text fontSize="md" fontWeight="semibold">
          Next Step
        </Text>

        <Flex dir="column" gap="md">
          <RedoSingleSelectDropdownInput<string>
            disabled={structureLocked}
            fitToAnchor
            label="After actions complete"
            onOptionMouseEnter={(item) => onItemHover(item.value)}
            onOptionMouseLeave={() => onItemHover(null)}
            options={availableSteps}
            optionSelected={({ item }) => {
              if (structureLocked) {
                return;
              }
              handleNextStepSelected(item.value);
            }}
            placeholder="Select a step or choose Unassign"
            selectedItem={
              nextStep
                ? availableSteps.find((opt) => opt.value === nextStep.id)
                : undefined
            }
          />
        </Flex>
      </Flex>
    </Flex>
  );
});

export const ruleActionsStepType: FrontendStepType<
  PartialWithRequiredType<RuleActionsStep>
> = {
  valid: (state) => {
    if (!state.actions || state.actions.length === 0) {
      return false;
    }

    // Check if any auto-reply action has empty text
    for (const action of state.actions) {
      if (action.actionType === RuleActionType.AUTO_REPLY) {
        const textContent = action.textContent?.trim() || "";
        if (textContent === "" || textContent === "\n") {
          return false;
        }
      }

      // Check if any add-tag or remove-tag action has no tags
      if (
        (action.actionType === RuleActionType.ADD_TAG ||
          action.actionType === RuleActionType.REMOVE_TAG) &&
        (!action.tag || action.tag.length === 0)
      ) {
        return false;
      }

      // Validate ASSIGN_V3: pool must not be empty
      if (action.actionType === RuleActionType.ASSIGN_V3) {
        const mode = action.mode;
        if (!mode || !mode._tag) {
          return false;
        }
        if (mode._tag === "specific_users") {
          if (!mode.mongoUserIds || mode.mongoUserIds.length === 0) {
            return false;
          }
        } else if (mode._tag === "user_group" && !mode.userGroupId) {
          return false;
        } else if (
          mode._tag === "actor" &&
          (!mode.assigneeTypes || mode.assigneeTypes.length === 0)
        ) {
          return false;
        }
      }
    }

    return true;
  },
  type: "custom",
  customInspector: RuleActionsInspector,
  updateConnection: (state, handleIndex, targetStepId) => {
    return produce(state, (draft) => {
      if (handleIndex === 0) {
        draft.nextId = targetStepId;
      } else {
        throw new Error(
          `Invalid handle index ${handleIndex} for Rule Actions step`,
        );
      }
    });
  },
  removeConnection: (state, targetStepId) => {
    return produce(state, (draft) => {
      if (draft.nextId === targetStepId) {
        draft.nextId = undefined;
      }
    });
  },
};
